import { usePreviewQuoteRecipientsQuery, useSetQuoteClientContactsMutation } from '@codezo/graphql';
import { QuotePeople, SectionHead } from '@codezo/quote-detail';
import { AlertTriangle, Mail, UserPlus, Users } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { ClientContactPickerModal } from '../../../components/quotes/modals/ClientContactPickerModal';
import { ContactDetails } from '../../../components/quotes/ContactDetails';
import { QuoteRecipientsLine } from '../../../components/quotes/QuoteRecipientsLine';
import { Button } from '../../../components/ui/button';
import { InitialsAvatar } from '../../../components/ui/initials-avatar';
import { useNominableClientContacts } from '../../../hooks/useNominableClientContacts';
import { getMutationError } from '../../../lib/mutation-response';

/** The backend's per-id refusal code on `setQuoteClientContacts`. */
const INVALID_CLIENT_CONTACT = 'INVALID_CLIENT_CONTACT';

/**
 * The state behind choosing a quote's client contacts: who is chosen, who will
 * actually be emailed, and the picker that changes it. Shared by the quote's
 * section below and the order page's people card, which lay it out differently
 * but must save and preview the same way.
 *
 * The quote only carries `clientContactUserIds`; names come from the client's
 * contact list, and who actually gets emailed comes from `previewQuoteRecipients`
 * — the backend's own rule, which also covers the fall-back to everyone and the
 * guest-link holders. Neither is worked out here.
 */
export function useClientContactsEditor({
	orderQuoteId,
	organizationId,
	clientContactUserIds,
	recipientsVersion,
	onSuccess,
}: {
	orderQuoteId: string;
	organizationId: string;
	clientContactUserIds: string[];
	/** Goes up whenever the quote or its guest links change — re-ask who gets emailed. */
	recipientsVersion: number;
	onSuccess: () => void;
}) {
	const [pickerOpen, setPickerOpen] = useState(false);
	const [refused, setRefused] = useState<Record<string, string>>({});

	const { contacts, loading: loadingContacts } = useNominableClientContacts(organizationId);

	const {
		data: previewData,
		loading: loadingPreview,
		error: previewError,
		refetch: refetchPreview,
	} = usePreviewQuoteRecipientsQuery({
		variables: { orderQuoteId },
		fetchPolicy: 'no-cache',
	});

	// The preview is computed live and not cached on the quote; the first
	// version is the mount fetch above.
	const seenVersion = useRef(recipientsVersion);
	useEffect(() => {
		if (seenVersion.current === recipientsVersion) return;
		seenVersion.current = recipientsVersion;
		void refetchPreview();
	}, [recipientsVersion, refetchPreview]);

	const [setContacts, { loading: saving }] = useSetQuoteClientContactsMutation();

	const preview = previewData?.previewQuoteRecipients;
	// Contacts are chosen but none of them still qualifies, so sends went org-wide.
	const listIgnored = clientContactUserIds.length > 0 && preview?.nominated === false;

	const handleSave = async (ids: string[]) => {
		try {
			const { data } = await setContacts({
				variables: { input: { orderQuoteId, organizationUserIds: ids } },
			});
			const response = data?.setQuoteClientContacts;
			const perId = (response?.errors ?? []).filter(
				(e) => e.code === INVALID_CLIENT_CONTACT && e.details
			);
			if (perId.length > 0) {
				// Nothing was saved; keep the picker open with the reason on each person.
				setRefused(Object.fromEntries(perId.map((e) => [e.details!, e.message])));
				toast.error('Some contacts can’t be chosen — nothing was saved.');
				return;
			}
			const error = getMutationError(response);
			if (error) {
				toast.error(error);
				return;
			}
			toast.success('Client contacts updated');
			setPickerOpen(false);
			// Refreshes the quote, which re-asks for the preview too.
			onSuccess();
		} catch {
			toast.error('Failed to update client contacts');
		}
	};

	const people = clientContactUserIds.map((id) => {
		const contact = contacts.find((c) => c.id === id);
		const name = contact?.name ?? (loadingContacts ? 'Loading…' : 'Can’t be emailed any more');
		return {
			id,
			name,
			meta: contact?.pending ? 'Invited' : null,
			details: contact && (contact.email || contact.phone) && (
				<ContactDetails email={contact.email} phone={contact.phone} />
			),
			avatar: <InitialsAvatar name={name} size={26} />,
		};
	});

	const openPicker = () => {
		setRefused({});
		setPickerOpen(true);
	};

	const recipients = (
		<div className="flex items-start gap-2 text-sm text-text-4">
			{listIgnored ? (
				<AlertTriangle size={14} className="mt-0.5 shrink-0 text-status-warn" />
			) : (
				<Mail size={14} className="mt-0.5 shrink-0" />
			)}
			<QuoteRecipientsLine
				loading={loadingPreview}
				failed={!!previewError}
				emails={preview?.emails}
				nominated={preview?.nominated}
				listIgnored={listIgnored}
			/>
		</div>
	);

	const picker = (
		<ClientContactPickerModal
			open={pickerOpen}
			onOpenChange={setPickerOpen}
			contacts={contacts}
			loadingContacts={loadingContacts}
			selectedIds={clientContactUserIds}
			onSave={handleSave}
			saving={saving}
			refused={refused}
		/>
	);

	return { people, openPicker, recipients, picker };
}

/**
 * Who at the client hears about this quote — every client email about it
 * (offers, deadlines, orders, samples, payments), not just offers.
 */
export function QuoteClientContactsV2({
	orderQuoteId,
	organizationId,
	organizationName,
	clientContactUserIds,
	recipientsVersion,
	onSuccess,
}: {
	orderQuoteId: string;
	organizationId: string;
	organizationName?: string | null;
	clientContactUserIds: string[];
	/** Goes up whenever the quote or its guest links change — re-ask who gets emailed. */
	recipientsVersion: number;
	onSuccess: () => void;
}) {
	const { people, openPicker, recipients, picker } = useClientContactsEditor({
		orderQuoteId,
		organizationId,
		clientContactUserIds,
		recipientsVersion,
		onSuccess,
	});

	return (
		<>
			<SectionHead
				title="Client contacts"
				meta="who at the client gets emails about this quote"
				action={
					<Button variant="ghost" size="sm" onClick={openPicker}>
						<UserPlus size={12} data-icon="inline-start" />
						Choose
					</Button>
				}
			/>
			<QuotePeople
				people={people}
				emptyIcon={<Users size={14} className="shrink-0 text-text-4" />}
				emptyLabel={`Nobody chosen — everyone at ${organizationName ?? 'the client'} is emailed.`}
			/>
			<div className="mt-2 px-1">{recipients}</div>
			{picker}
		</>
	);
}
